import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarClock, Download, FileText, Loader2, Mail, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { authFetch } from "@/lib/authFetch";
import { useAuth } from "@/hooks/use-auth";
import { useSyncPlanningWeekCalendar } from "@/hooks/use-planning";
import { useAgences } from "@/hooks/use-agences";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const WEEKLY_PLANNING_PDF_ENDPOINT = "/api/planning/pdf";
export const WEEKLY_PLANNING_RECIPIENTS_ENDPOINT = "/api/planning/pdf/recipients";
export const WEEKLY_PLANNING_SEND_ENDPOINT = "/api/planning/pdf/send";

type WeeklyPdfRecipient = {
  email: string;
  name?: string | null;
};

type WeeklyPdfActionsProps = {
  weekStart: Date;
  missions: any[];
  canManage: boolean;
};

function weekQuery(start: Date): string {
  return format(start, "yyyy-MM-dd");
}

function weekLabel(start: Date): string {
  const end = new Date(start);
  end.setDate(end.getDate() + 4);
  return `${format(start, "d", { locale: fr })} au ${format(end, "d MMMM yyyy", { locale: fr })}`;
}

function subjectWeekLabel(start: Date): string {
  const end = new Date(start);
  end.setDate(end.getDate() + 4);
  return `${format(start, "d", { locale: fr })} au ${format(end, "d MMMM", { locale: fr })}`;
}

function defaultBody(start: Date, missionCount?: number): string {
  const countLine = missionCount === undefined ? "" : `\n${missionCount} mission${missionCount > 1 ? "s" : ""} sont planifiée${missionCount > 1 ? "s" : ""}.`;
  return `Bonjour,\n\nVeuillez trouver ci-joint le planning technique de la semaine du ${weekLabel(start)}.${countLine}\n\nBonne journée,\nL'équipe technique`;
}

function parisDateKey(value: string | Date): string | null {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function addDateKeyDays(value: string, days: number): string {
  const date = new Date(`${value}T12:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

async function readError(response: Response): Promise<string> {
  try {
    const payload = await response.json();
    return payload?.message || payload?.error || `Erreur ${response.status}`;
  } catch {
    return `Erreur ${response.status}`;
  }
}

export function WeeklyPdfActions({ weekStart, missions, canManage }: WeeklyPdfActionsProps) {
  const { toast } = useToast();
  const { profile } = useAuth();
  const calendarSync = useSyncPlanningWeekCalendar();
  const { data: agences = [] } = useAgences();
  const [agencyCode, setAgencyCode] = useState<"ALL" | "IDF" | "SUD">("ALL");
  const [isPreparing, setIsPreparing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<WeeklyPdfRecipient[]>([]);
  const [recipientInput, setRecipientInput] = useState("");
  const [body, setBody] = useState("");
  const [confirmSend, setConfirmSend] = useState(false);

  const week = weekQuery(weekStart);
  const canSyncCalendar = ["admin", "super_admin"].includes((profile as any)?.accessRole);
  const missionCount = useMemo(() => {
    const saturday = addDateKeyDays(week, 5);
    return (missions || []).filter(mission => {
      const day = parisDateKey(mission.startDate);
      return day !== null && day >= week && day < saturday;
    }).length;
  }, [missions, week]);
  const optionCount = useMemo(
    () => {
      const saturday = addDateKeyDays(week, 5);
      return (missions || []).filter(mission => {
        const day = parisDateKey(mission.startDate);
        return mission.status === "option" && day !== null && day >= week && day < saturday;
      }).length;
    },
    [missions, week],
  );
  const subject = `[SERVICE TECHNIQUE${agencyCode !== "ALL" ? ` · ${agencyCode}` : ""}] - Organisation de la semaine du ${subjectWeekLabel(weekStart)}.`;

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  if (!canManage) return null;

  const fetchPdf = async (): Promise<string> => {
    const params = new URLSearchParams({ startDate: week });
    if (agencyCode !== "ALL") params.set("agenceCode", agencyCode);
    const response = await authFetch(`${WEEKLY_PLANNING_PDF_ENDPOINT}?${params}`);
    if (!response.ok) throw new Error(await readError(response));
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  };

  const downloadAndPreview = async () => {
    setIsLoading(true);
    try {
      const url = await fetchPdf();
      const link = document.createElement("a");
      link.href = url;
      link.download = `planning-technique-${week}${agencyCode !== "ALL" ? `-${agencyCode.toLowerCase()}` : ""}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      toast({ variant: "destructive", title: "Génération impossible", description: error instanceof Error ? error.message : "Erreur inattendue" });
    } finally {
      setIsLoading(false);
    }
  };

  const addRecipient = () => {
    const email = recipientInput.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if (!recipients.some(recipient => recipient.email.toLowerCase() === email)) {
      setRecipients(previous => [...previous, { email }]);
    }
    setRecipientInput("");
  };

  const openPreparation = async () => {
    setIsPreparing(true);
    setIsLoading(true);
    setBody(defaultBody(weekStart, agencyCode === "ALL" ? missionCount : undefined));
    try {
      const [url, recipientResponse] = await Promise.all([
        fetchPdf(),
        authFetch(WEEKLY_PLANNING_RECIPIENTS_ENDPOINT),
      ]);
      if (!recipientResponse.ok) throw new Error(await readError(recipientResponse));
      const payload = await recipientResponse.json();
      const initialRecipients = Array.isArray(payload) ? payload : payload.recipients;
      setRecipients(Array.isArray(initialRecipients) ? initialRecipients.filter(item => item?.email).map(item => ({ email: item.email, name: item.name })) : []);
      setPdfUrl(previous => {
        if (previous) URL.revokeObjectURL(previous);
        return url;
      });
    } catch (error) {
      setIsPreparing(false);
      toast({ variant: "destructive", title: "Préparation impossible", description: error instanceof Error ? error.message : "Erreur inattendue" });
    } finally {
      setIsLoading(false);
    }
  };

  const sendMail = async () => {
    if (recipients.length === 0) {
      toast({ variant: "destructive", title: "Destinataire requis", description: "Ajoutez au moins une adresse email." });
      return;
    }
    setIsSending(true);
    try {
      const response = await authFetch(WEEKLY_PLANNING_SEND_ENDPOINT, {
        method: "POST",
        body: JSON.stringify({
          startDate: week,
            ...(agencyCode !== "ALL" ? { agenceCode: agencyCode } : {}),
          recipients: recipients.map(recipient => recipient.email),
          subject,
          body,
        }),
      });
      if (!response.ok) throw new Error(await readError(response));
      setConfirmSend(false);
      setIsPreparing(false);
      toast({ title: "Planning envoyé", description: `${recipients.length} destinataire${recipients.length > 1 ? "s" : ""} confirmé${recipients.length > 1 ? "s" : ""}.` });
    } catch (error) {
      toast({ variant: "destructive", title: "Envoi impossible", description: error instanceof Error ? error.message : "Erreur inattendue" });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-1.5">
        <Select value={agencyCode} onValueChange={value => setAgencyCode(value as typeof agencyCode)}>
          <SelectTrigger className="h-8 w-[108px] text-xs" data-testid="select-week-pdf-agency"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">Toutes agences</SelectItem>{agences.map(agence => <SelectItem key={agence.code} value={agence.code}>{agence.code}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={downloadAndPreview} disabled={isLoading} data-testid="btn-generate-week-pdf">
          {isLoading && !isPreparing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Générer
        </Button>
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={openPreparation} disabled={isLoading} data-testid="btn-send-week-pdf">
          <Mail className="h-3.5 w-3.5" />
          Envoyer
        </Button>
        {canSyncCalendar && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={() => calendarSync.mutate(week)}
            disabled={calendarSync.isPending}
            data-testid="btn-sync-google-calendar"
          >
            {calendarSync.isPending
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <CalendarClock className="h-3.5 w-3.5" />}
            Google Agenda
          </Button>
        )}
      </div>

      {calendarSync.data && (
        <div className="rounded-lg border bg-muted/40 px-3 py-2 text-xs" data-testid="calendar-sync-report">
          <p className="font-medium">
            Google Agenda : {calendarSync.data.created} créé(s), {calendarSync.data.updated} mis à jour,
            {" "}{calendarSync.data.deleted} supprimé(s), {calendarSync.data.unchanged} inchangé(s),
            {" "}{calendarSync.data.errors.length} en erreur.
          </p>
          {calendarSync.data.errors.length > 0 && (
            <ul className="mt-1 list-disc pl-4 text-destructive">
              {calendarSync.data.errors.map(error => (
                <li key={error.missionId}><strong>{error.title}</strong> — {error.message}</li>
              ))}
            </ul>
          )}
          {calendarSync.data.warnings.length > 0 && (
            <ul className="mt-1 list-disc pl-4 text-amber-700">
              {calendarSync.data.warnings.flatMap(warning =>
                warning.messages.map((message, index) => (
                  <li key={`${warning.missionId}-${index}`}><strong>{warning.title}</strong> — {message}</li>
                )),
              )}
            </ul>
          )}
        </div>
      )}
      {calendarSync.error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {calendarSync.error.message}
        </div>
      )}

      <Dialog open={isPreparing} onOpenChange={open => !open && setIsPreparing(false)}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Préparer l'envoi du planning</DialogTitle>
            <DialogDescription>
              Semaine du {weekLabel(weekStart)} · {missionCount} mission{missionCount > 1 ? "s" : ""}
              {optionCount > 0 && ` · ${optionCount} en option`}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><FileText className="h-4 w-4" /> Aperçu PDF</Label>
              <div className="rounded-lg border bg-muted/20 overflow-hidden min-h-[420px]">
                {pdfUrl ? (
                  <iframe src={pdfUrl} title="Aperçu du planning PDF" className="w-full h-[min(65vh,620px)]" />
                ) : (
                  <div className="h-[420px] flex items-center justify-center text-sm text-muted-foreground">
                    {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Aucun aperçu disponible"}
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">Le document est généré depuis les missions actuelles et n'est pas modifiable ici.</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Destinataires</Label>
                <div className="flex flex-wrap gap-1.5 rounded-lg border p-2 min-h-10">
                  {recipients.map(recipient => (
                    <span key={recipient.email} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-xs">
                      {recipient.name ? `${recipient.name} · ` : ""}{recipient.email}
                      <button type="button" onClick={() => setRecipients(previous => previous.filter(item => item.email !== recipient.email))} aria-label={`Retirer ${recipient.email}`}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <Input
                    value={recipientInput}
                    onChange={event => setRecipientInput(event.target.value)}
                    onKeyDown={event => { if (event.key === "Enter" || event.key === "," ) { event.preventDefault(); addRecipient(); } }}
                    onBlur={addRecipient}
                    placeholder="Ajouter une adresse email"
                    className="h-7 min-w-[180px] flex-1 border-0 shadow-none focus-visible:ring-0 text-xs"
                    type="email"
                  />
                </div>
                <p className="text-xs text-muted-foreground">La liste initiale est modifiable. Les adresses ajoutées ne sont pas enregistrées.</p>
              </div>

              <div className="space-y-2">
                <Label>Objet</Label>
                <Input value={subject} readOnly className="text-sm bg-muted/30" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="weekly-mail-body">Corps du message</Label>
                <Textarea id="weekly-mail-body" value={body} onChange={event => setBody(event.target.value)} rows={10} className="resize-y text-sm" />
              </div>
              <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
                {missionCount} mission{missionCount > 1 ? "s" : ""} seront jointes au format PDF.
                {optionCount > 0 && ` Dont ${optionCount} en option.`}
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2">
            <Button variant="ghost" onClick={() => setIsPreparing(false)}>Retour à la semaine</Button>
            <Button onClick={() => setConfirmSend(true)} disabled={isSending || recipients.length === 0} className="gap-1.5" data-testid="btn-confirm-week-pdf-send">
              <Send className="h-4 w-4" /> Confirmer l'envoi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmSend} onOpenChange={setConfirmSend}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer l'envoi du planning</AlertDialogTitle>
            <AlertDialogDescription>
              Le PDF sera envoyé à {recipients.length} destinataire{recipients.length > 1 ? "s" : ""}. Cette action ne modifie aucune mission.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSending}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={event => { event.preventDefault(); void sendMail(); }} disabled={isSending} className="gap-1.5">
              {isSending && <Loader2 className="h-4 w-4 animate-spin" />} Envoyer maintenant
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}